<script>
// Resumo dos critérios no gráfico de barras horizontais da plataforma
// (bar-horizontal-chart, chart.js v2) montado pelo helper GraficoData
// `formatBarsHorizontal_v2`. Barra empilhada de 2 grupos: "Nota" (preenchimento
// colorido por faixa, hex pois canvas não resolve var(--x)) + "Restante" (10 −
// nota, em cinza claro = trilha) → total 10 em toda barra → trilha cheia
// (percepção de vazio) e escala fixa 0–10. Os nomes dos critérios são rótulos
// HTML à esquerda (chart.js v2 não alinha rótulos do eixo Y à esquerda), com a
// mesma altura de linha das barras. Não renderiza card nem título — o wrapper
// externo (TranscriptSummaryCard) cuida disso.
import { formatBarsHorizontal_v2 } from '@utils/GraficoData'

const MAX_SCORE = 10
const ROW_HEIGHT = 46
const TRACK_HEX = '#EFEDF1' // trilha clara (parte vazia), pouco mais escura que o fundo
const TRANSPARENT = 'rgba(0,0,0,0)'

export default {
  name: 'TranscriptCriteriaBars',
  props: {
    // Cada item: { id, name, score, colorHex, scoreLabel, ... }
    criteria: { type: Array, default: () => [] },
  },
  data() {
    return { rowHeight: ROW_HEIGHT }
  },
  computed: {
    // Há ao menos um critério com nota numérica.
    hasBars() {
      return this.criteria.some((item) => item && item.score !== null)
    },
    // `dados` de entrada do helper: 2 grupos empilhados — Nota (preenchimento
    // colorido por faixa) + Restante (trilha clara). Restante sem rótulo de cor.
    chartDados() {
      const toRest = (item) =>
        item.score === null
          ? MAX_SCORE
          : Math.max(0, Math.round((MAX_SCORE - item.score) * 100) / 100)
      return {
        mode: 'numero',
        legend: false,
        data: {
          labels: this.criteria.map((item) => item.name),
          datasets: {
            Nota: {
              dados: this.criteria.map((i) => (i.score === null ? 0 : i.score)),
              colors: this.criteria.map((item) => item.colorHex),
              labelColor: '#FFF',
            },
            Restante: {
              dados: this.criteria.map(toRest),
              color: TRACK_HEX,
              labelColor: TRANSPARENT,
            },
          },
        },
      }
    },
    // Config completa (data + options + plugins) do bar-horizontal-chart.
    chartConfig() {
      const config = formatBarsHorizontal_v2(this.chartDados, true)
      // A trilha (Restante) não exibe rótulo de valor (flag lida pelo plugin).
      if (config.chartData.datasets[1]) {
        config.chartData.datasets[1].hideValueLabel = true
      }
      this.applyChartOptions(config.chartOptions)
      return config
    },
    chartHeight() {
      return Math.max(ROW_HEIGHT * this.criteria.length, ROW_HEIGHT)
    },
  },
  methods: {
    // Ajustes sobre as options do helper: empilhamento, escala fixa 0–10, barra
    // com cantos só nos extremos, sem tooltip/eventos (estático) e eixo Y sem
    // rótulos (os nomes ficam em HTML).
    applyChartOptions(options) {
      options.scales.xAxes[0].stacked = true
      options.scales.yAxes[0].stacked = true
      options.scales.xAxes[0].ticks.min = 0
      options.scales.xAxes[0].ticks.max = MAX_SCORE
      options.legend = { display: false }
      // Cantos arredondados só nos extremos da barra (junção fill/trilha reta).
      options.cornerRadius = 4
      options.cornerRadiusOuterOnly = true
      options.tooltips = { enabled: false }
      // Mantém o hover, mas sem animação (animationDuration 0) → o realce é
      // instantâneo e o chart não fica re-animando a cada mousemove (era o que
      // causava o flicker sobre a barra).
      options.hover = { animationDuration: 0 }
      options.scales.yAxes[0].ticks.display = false
      options.scales.yAxes[0].afterFit = (scale) => {
        scale.width = 0
      }
      // Eixo X totalmente oculto → não reserva altura, as bandas das barras
      // batem exatamente com as linhas (ROW_HEIGHT) dos nomes em HTML.
      options.scales.xAxes[0].display = false
      options.layout = { padding: { top: 0, bottom: 0, left: 0, right: 28 } }
    },
  },
}
</script>

<template>
  <div v-if="hasBars" class="transcript-criteria-bars d-flex">
    <!-- Nomes dos critérios à esquerda, alinhados às barras -->
    <div class="transcript-criteria-bars__names">
      <div
        v-for="item in criteria"
        :key="item.id"
        class="transcript-criteria-bars__name d-flex align-center"
        :style="{ height: rowHeight + 'px' }"
      >
        <span class="az-text-primary az-size-14 text-truncate">
          {{ item.name }}
        </span>
      </div>
    </div>

    <!-- Gráfico (apenas as barras) -->
    <div class="flex-grow-1">
      <bar-horizontal-chart
        :chart-data="chartConfig.chartData"
        :options="chartConfig.chartOptions"
        :plugins="chartConfig.chartPlugins"
        :height="chartHeight"
      />
    </div>
  </div>
</template>

<style lang="sass" scoped>
.transcript-criteria-bars
  &__names
    flex: 0 0 38%
    max-width: 38%
    padding-right: 16px

  &__name
    overflow: hidden
</style>
